import { MenuItem, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from 'utils/helpers';
import { AccountingInvolvedParty } from 'v2-types/order';

const useStyles = makeStyles((theme: any) => ({
  input: {
    '&:focus': { backgroundColor: theme.palette.background.default },
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.common.black,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: theme.palette.background.default,
    },
    '& .Mui-disabled': { cursor: 'not-allowed' }
  },
  naked: {
    '&:focus': { backgroundColor: theme.palette.background.default },
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.common.black,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: 'transparent',
    },
    '& .MuiFilledInput-input': {
      paddingTop: 10,
      paddingLeft: 0,
    },
    '& .MuiOutlinedInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.common.black,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: 'transparent',
    },
    '& .Mui-disabled': { cursor: 'not-allowed' }
  },
}));

type customElementProps<K> = {
  element: K,
}

type SelectFieldProps<T> = {
  emptyValue?: boolean;
  naked?: boolean;
  name?: string;
  label?: string;
  value: string;
  data: T[];
  error?: boolean;
  helperText?: string;
  handleBlur?: any;
  handleChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  InputProps?: any;
  style?: {};
  disabled?: boolean;
  inputRef?: any;
  CustomElement?: React.ComponentType<customElementProps<T>>
};

// eslint-disable-next-line comma-spacing
const SelectFieldLink = <T,>({
  emptyValue,
  naked,
  name,
  label,
  value,
  data,
  error,
  helperText,
  handleBlur,
  handleChange,
  InputProps,
  style,
  disabled,
  inputRef,
  CustomElement
}: SelectFieldProps<T>) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <TextField
      style={style}
      select
      fullWidth
      variant="filled"
      name={name}
      label={label}
      className={clsx({ [classes.input]: !naked, [classes.naked]: naked })}
      value={value}
      error={error}
      helperText={helperText}
      InputProps={{ disableUnderline: naked, ...InputProps }}
      onBlur={handleBlur}
      onChange={handleChange}
      disabled={disabled}
      inputRef={inputRef}
    >
      {emptyValue && (
        <MenuItem value="">
          {t('common:select-one')}
        </MenuItem>
      )}

      {CustomElement && data.map((element) => (
        <MenuItem
          key={(element as any)._id}
          disabled={disabled}
          value={(element as any)._id}
        >

          <CustomElement
            element={element}
          />
        </MenuItem>
      ))}
    </TextField>
  );
};

SelectFieldLink.defaultProps = {
  emptyValue: true,
  naked: false,
  name: undefined,
  label: '',
  error: false,
  helperText: undefined,
  handleBlur: undefined,
  handleChange: () => { },
  InputProps: {},
  style: {},
  disabled: false,
  inputRef: undefined,
};

export default SelectFieldLink;
